import { ref } from 'vue'
import { defineStore } from 'pinia'

import { BaseError, camelCaseKeysToUnderscore, underscoreKeysToCamelCase } from '@ezio/utils'
import { useEzioAPI } from '@ezio/services'

import {
    CatalogOverrides,
    EffectivePermissions,
    ManagerRoles,
    PermissionCatalog,
    Role,
    RoleAssignment,
    RoleAssignmentUpdate,
    RoleInput
} from './types'

/**
 * Routes API consommées :
 *   GET    /v1/permission_catalog                (?agency_id= | ?raw=true)
 *   GET    /v1/permission_catalog/overrides
 *   PUT    /v1/permission_catalog/overrides
 *   GET    /v1/roles                             (?account_id= : super admin)
 *   GET    /v1/roles/:id
 *   POST   /v1/roles                             body: { name, description, permissions }
 *   PUT    /v1/roles/:id                         body: { name?, description?, permissions? }
 *   DELETE /v1/roles/:id
 *   GET    /v1/managers/:id/permissions
 *   PUT    /v1/managers/:id/permissions       body: { role_id } | { role_assignments }
 *
 * Ces endpoints ne sont pas au format JSON:API : le deserializer les laisse
 * tels quels (snake_case). C'est donc `response.data` qu'on convertit en
 * camelCase. Les clés numériques (id d'agence type "42") ne sont pas touchées
 * par les converters de @ezio/utils. En revanche ces converters ne gèrent pas
 * les tableaux (ils les transforment en objets indexés) : on convertit donc les
 * listes élément par élément et on recopie les tableaux de scalaires tels quels.
 */
export const usePermissionsStore = defineStore('permissions', () => {
    const { api } = useEzioAPI()

    const catalog = ref<PermissionCatalog>(null)
    // le catalogue porte des libellés adaptés à l'agence : cache par agence
    const catalogAgencyId = ref<string>(null)

    // cache de la liste des rôles du compte (invalidé à chaque écriture) ;
    // un super admin peut consulter une autre organisation : le cache retient
    // le compte interrogé pour ne pas resservir les rôles d'un autre
    const roles = ref<Role[]>(null)
    const rolesAccountId = ref<string>(null)

    // raw : libellés bruts (placeholders non résolus), pour l'éditeur du catalogue
    const fetchCatalog = async (
        agencyId?: string | number,
        options?: { raw?: boolean }
    ): Promise<PermissionCatalog> => {
        const raw = !!options?.raw
        const key = raw ? '#raw' : agencyId != null ? String(agencyId) : null
        if (catalog.value && catalogAgencyId.value === key) {
            return catalog.value
        }
        try {
            const response = await api.get(
                'permission_catalog',
                raw ? { raw: true } : key ? { agency_id: key } : {}
            )
            catalog.value = underscoreKeysToCamelCase(response.data) as PermissionCatalog
            catalogAgencyId.value = key
            return catalog.value
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    // `user_ids` est un tableau de scalaires : le converter le muterait en objet
    // indexé, on le recopie donc après coup
    const toRole = (raw: any): Role => {
        const role = underscoreKeysToCamelCase(raw) as Role
        const userIds = raw?.user_ids ?? raw?.userIds
        if (Array.isArray(userIds)) {
            role.userIds = userIds
        }
        return role
    }

    const toManagerRoles = (raw: any): ManagerRoles => {
        const assignments = raw?.role_assignments ?? raw?.roleAssignments
        return {
            userId: raw?.user_id ?? raw?.userId,
            orga: !!raw?.orga,
            roleAssignments: (Array.isArray(assignments) ? assignments : []).map(
                (assignment: any) => underscoreKeysToCamelCase(assignment)
            ) as RoleAssignment[],
            effective: (underscoreKeysToCamelCase(raw?.effective) ?? {}) as EffectivePermissions
        }
    }

    const fetchFor = async (managerId: string | number): Promise<ManagerRoles> => {
        try {
            const response = await api.get('managers/' + managerId + '/permissions')
            return toManagerRoles(response.data)
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    /**
     * Attribution des rôles d'un manager. Deux formes exclusives :
     *   { roleId }           → un rôle à portée compte
     *   { roleAssignments }  → { agencyId: roleId }, un rôle par agence
     */
    const assignRoles = async (
        managerId: string | number,
        payload: RoleAssignmentUpdate
    ): Promise<ManagerRoles> => {
        try {
            const body = camelCaseKeysToUnderscore(payload)
            const response = await api.put('managers/' + managerId + '/permissions', body)
            // les usersCount de la liste en cache ne sont plus justes : on la
            // rafraîchit en arrière-plan, sans vider la liste affichée (les
            // selects ouverts la lisent de façon réactive)
            fetchRoles({ force: true, accountId: rolesAccountId.value ?? undefined }).catch(
                () => {}
            )
            return toManagerRoles(response.data)
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    /**
     * Rôles de l'organisation : seul l'administrateur du compte (systemKey
     * account_admin) est verrouillé — ni modifiable ni supprimable. Les autres
     * rôles de base (agency_admin, agency_manager) se modifient et se
     * suppriment comme les rôles personnalisés.
     */
    const fetchRoles = async (options?: {
        force?: boolean
        // super admin : rôles d'une autre organisation que celle de la session
        accountId?: string | number
    }): Promise<Role[]> => {
        const accountKey = options?.accountId != null ? String(options.accountId) : null
        if (roles.value && !options?.force && rolesAccountId.value === accountKey) {
            return roles.value
        }
        try {
            const response = await api.get('roles', accountKey ? { account_id: accountKey } : {})
            const body: any = response.data
            const list: any[] = Array.isArray(body) ? body : (body?.roles ?? [])
            roles.value = list.map(toRole)
            rolesAccountId.value = accountKey
            return roles.value
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const fetchRole = async (roleId: string | number): Promise<Role> => {
        try {
            const response = await api.get('roles/' + roleId)
            return toRole(response.data)
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const createRole = async (input: RoleInput): Promise<Role> => {
        try {
            const response = await api.post('roles', camelCaseKeysToUnderscore(input))
            roles.value = null
            return toRole(response.data)
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const updateRole = async (roleId: string | number, input: Partial<RoleInput>): Promise<Role> => {
        try {
            const response = await api.put('roles/' + roleId, camelCaseKeysToUnderscore(input))
            roles.value = null
            return toRole(response.data)
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    // 403 sur un rôle système, 400 tant que des managers portent encore ce rôle
    const deleteRole = async (roleId: string | number): Promise<void> => {
        try {
            await api.delete('roles/' + roleId)
            roles.value = null
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    /**
     * Personnalisation d'affichage du catalogue (super admin uniquement) :
     * libellés et positions, rien d'autre. Le PUT remplace intégralement le
     * sparse stocké ; le catalogue servi ensuite intègre ces personnalisations.
     */
    const fetchCatalogOverrides = async (): Promise<CatalogOverrides> => {
        try {
            const response = await api.get('permission_catalog/overrides')
            const body = underscoreKeysToCamelCase(response.data) as { overrides: CatalogOverrides }
            return body?.overrides ?? {}
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const updateCatalogOverrides = async (
        overrides: CatalogOverrides
    ): Promise<CatalogOverrides> => {
        try {
            const response = await api.put(
                'permission_catalog/overrides',
                camelCaseKeysToUnderscore({ overrides })
            )
            // les libellés/positions servis par le catalogue viennent de changer
            catalog.value = null
            catalogAgencyId.value = null
            const body = underscoreKeysToCamelCase(response.data) as { overrides: CatalogOverrides }
            return body?.overrides ?? {}
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const reset = () => {
        catalog.value = null
        catalogAgencyId.value = null
        roles.value = null
    }

    return {
        catalog,
        roles,
        fetchCatalog,
        fetchFor,
        assignRoles,
        fetchRoles,
        fetchRole,
        createRole,
        updateRole,
        deleteRole,
        fetchCatalogOverrides,
        updateCatalogOverrides,
        reset
    }
})
